import { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Outlet, useNavigate } from 'react-router-dom';

import { CompanyContext } from './use-company-context';

import { ActiveSubscriptionContext } from '@/components/app-shell';
import { ActivateSubscription } from '@/components/activate-subscription/activate-subscription.component';
import { CompanyHeaderBanner } from '@/components/company-details/company-header-banner.component';
import {
  Company,
  CompanyNotFoundError,
  getCompany,
} from '@/services/hsm-backend';

type FetchState =
  | { kind: 'loading' }
  | { kind: 'success'; company: Company }
  | { kind: 'no-subscription'; company: Company }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string };

// A loaded company without a subscription routes to the "Activar suscripción"
// screen instead of the operative detail (GET /company now returns 200 with
// `subscription: null` for that case; 404 stays for a nonexistent company).
const stateForCompany = (company: Company): FetchState =>
  company.subscription
    ? { kind: 'success', company }
    : { kind: 'no-subscription', company };

export interface CompanyShellProps {
  companyId: string;
  onCompanyLoaded?: (company: Company) => void;
}

/**
 * Layout component shared by every `/company/:companyId/*` route. Owns
 * the single `getCompany()` call for the company, renders the sticky
 * `CompanyHeaderBanner` on success, and exposes the loaded company plus
 * a `refetch` handle to the child route via `<Outlet context={…}>`.
 *
 * Children read this through the `useCompanyContext()` hook. Forms call
 * `refetch()` after a successful POST so the banner and the "current"
 * card on the left of each tab show the new state without a reload.
 *
 * @param companyId       ID of the company to load (typically from the URL).
 * @param onCompanyLoaded Optional callback fired once the company is fetched.
 */
export const CompanyShell = ({
  companyId,
  onCompanyLoaded,
}: CompanyShellProps) => {
  const [state, setState] = useState<FetchState>({ kind: 'loading' });
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setHasActiveSubscription } = useContext(ActiveSubscriptionContext);

  const load = useCallback(async () => {
    try {
      const company = await getCompany(companyId);

      setState(stateForCompany(company));
      onCompanyLoaded?.(company);
    } catch (error) {
      if (error instanceof CompanyNotFoundError) {
        setState({ kind: 'not-found' });
        return;
      }
      const message =
        error instanceof Error ? error.message : 'Error desconocido';

      setState({ kind: 'error', message });
    }
  }, [companyId, onCompanyLoaded]);

  // A refetch backs a successful POST — a transient failure must NOT tear
  // the whole screen down to the error state (the write already happened
  // and the user saw a success toast). Keep the current company mounted
  // and just surface a warning toast.
  const refetch = useCallback(async () => {
    try {
      const company = await getCompany(companyId);

      setState(stateForCompany(company));
    } catch {
      enqueueSnackbar('No se pudo refrescar la empresa', {
        variant: 'warning',
      });
    }
  }, [companyId, enqueueSnackbar]);

  useEffect(() => {
    setState({ kind: 'loading' });
    void load();
  }, [load]);

  // Publish to `AppShell` (which renders the tabs above this route) whether
  // the loaded company has an *active* subscription. Billing needs an active
  // one — a present-but-inactive subscription still lands on the operative
  // detail (the banner flags it "inactiva") but keeps the billing tabs
  // locked. Reset on unmount so leaving for the search screen re-locks them.
  const hasActiveSubscription =
    state.kind === 'success' && state.company.subscription?.status === 'active';

  useEffect(() => {
    setHasActiveSubscription(hasActiveSubscription);
    return () => setHasActiveSubscription(false);
  }, [hasActiveSubscription, setHasActiveSubscription]);

  const handleChangeCompany = () => navigate('/');

  if (state.kind === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label="loading" />
      </Box>
    );
  }

  if (state.kind === 'not-found') {
    return (
      <ErrorBody>
        <Alert severity="warning">
          No se encontró empresa con id <strong>{companyId}</strong>.
        </Alert>
      </ErrorBody>
    );
  }

  if (state.kind === 'error') {
    return (
      <ErrorBody>
        <Stack spacing={2}>
          <Alert severity="error">
            No se pudo obtener la empresa: {state.message}
          </Alert>
          <Box>
            <Button variant="outlined" onClick={() => void load()}>
              Reintentar
            </Button>
          </Box>
        </Stack>
      </ErrorBody>
    );
  }

  if (state.kind === 'no-subscription') {
    return (
      <>
        <CompanyHeaderBanner
          company={state.company}
          onRefresh={() => void load()}
          onChangeCompany={handleChangeCompany}
        />
        <ActivateSubscription
          companyId={state.company.companyId}
          channels={state.company.channels}
          refetch={refetch}
        />
      </>
    );
  }

  const context: CompanyContext = { company: state.company, refetch };

  return (
    <>
      <CompanyHeaderBanner
        company={state.company}
        onRefresh={() => void load()}
        onChangeCompany={handleChangeCompany}
      />
      <Outlet context={context} />
    </>
  );
};

const ErrorBody = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 6 }}>{children}</Box>
);
